import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Text from "../../../../utils/Custom/text";
import CommonForm from "../../../../utils/commonComponent/commonForm";
import { CancelButton, SaveAndUpdate } from "../../../../utils/Custom/Button";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import axiosWithToken from "../../../../utils/commonComponent/axiosTokenConfig";       
import { BASE_URL } from "../../../../global";
import { getPermissions } from "../../../Pages/rbac/hasAccess";
import { validateRequiredFields } from "../../../../utils/commonComponent/errorAlert";

const EditRequirements = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const navigate = useNavigate();
  
  const [inputStates, setInputStates] = React.useState({
    RequestStatus: 1,
    status: 1,
    DateRaised: new Date().toISOString().split("T")[0]
  });

  const [disciplines, setDisciplines] = React.useState([]);
  const [employmentTypes, setEmploymentTypes] = React.useState([]);
  const [reqStatuses, setReqStatuses] = React.useState([]);
  const [priorities, setPriorities] = React.useState([]);
  const [recruiters, setRecruiters] = React.useState([]);
  const [positionOptions, setPositionOptions] = React.useState([]);

  const updateInputObject = (key, value) => {
    setInputStates((prev) => ({ ...prev, [key]: value }));
  };

  const fetchNextRequestedId = async () => {
    try {
      const [last] = await fetchQuery(
        `SELECT requestedId FROM talentAcqRequirement ORDER BY id DESC LIMIT 1`
      );
      const lastId = parseInt(
        last?.requestedId?.match(/\d+$/)?.[0] || "0",
        10
      );
      const nextId = `REQ-${String(lastId + 1).padStart(3, "0")}`;
      updateInputObject("requestedId", nextId);
    } catch (error) {
      console.error("Error fetching last Requested ID:", error);
      updateInputObject("requestedId", "REQ-001");
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const [masters, recruitersData] = await Promise.all([
        fetchQuery(
          "SELECT id, type, Value FROM talentAcquisitionMaster WHERE status != -1"
        ),
        fetchQuery(
          `SELECT e.id, e.employeesCode, e.name FROM employees e LEFT JOIN employeeOtherData eod ON eod.employeeId = e.id WHERE e.status = 1 AND (eod.hrDesignation = 'Head -HR' OR eod.hrDesignation = 'HR Executive')`
        )
      ]);
      const list = masters || [];
      
      setDisciplines(list.filter(m => m.type == 1).map(m => ({ name: m.Value })));
      setEmploymentTypes(list.filter(m => m.type == 2).map(m => ({ name: m.Value })));
      setReqStatuses(list.filter(m => m.type == 3).map(m => ({ id: m.id, name: m.Value })));
      setPriorities(list.filter(m => m.type == 4).map(m => ({ name: m.Value })));
      setPositionOptions(list.filter(m => m.type == 13).map(m => ({ name: m.Value })));
      setRecruiters((recruitersData || []).map(r => ({
        id: r.id,
        name: r.employeesCode ? `${r.employeesCode} - ${r.name}` : r.name
      })));
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  const fetchDetails = async () => {
    try {
      const [record] = await fetchQuery(
        `SELECT * FROM talentAcqRequirement WHERE id = ${params.id}`
      );
      if (record) {
        if (record.DateRaised) record.DateRaised = record.DateRaised.split("T")[0];
        if (record.targetDate) record.targetDate = record.targetDate.split("T")[0];
        setInputStates(record);
      }
    } catch (error) {
      console.error("Error fetching requirement data:", error);
    }
  };

  const onSubmit = async () => {
    const fieldsToValidate = [
      { value: inputStates.requestedId, label: "Requested ID is required" },
      { value: inputStates.DateRaised, label: "Date Raised is required" },
      { value: inputStates.Client, label: "Client is required" },
      { value: inputStates.positionTitle, label: "Position Title is required" },
      { value: inputStates.Discipline, label: "Discipline is required" },
      { value: inputStates.employementType, label: "Employment Type is required" },
      { value: inputStates.location, label: "Location is required" },
      { value: inputStates.openingNo, label: "No. of Openings is required" },
      { value: inputStates.budetMin, label: "Budget Min is required" },
      { value: inputStates.budgetMax, label: "Budget Max is required" },
      { value: inputStates.jdRef, label: "JD Reference is required" },
      { value: inputStates.priority, label: "Priority is required" },
      { value: inputStates.targetDate, label: "Target Date is required" },
      { value: inputStates.RequestStatus, label: "Request Status is required" },
      { value: inputStates.recruiterAssigned, label: "Recruiter Assigned is required" },
    ];

    if (!validateRequiredFields(fieldsToValidate)) return;

    try {
      const url = BASE_URL + `talentAcqRequirement`;
      params.id != 0
        ? await axiosWithToken.patch(`${url}/${params.id}`, inputStates)
        : await axiosWithToken.post(url, inputStates);

      toast.success(
        params.id != 0 ? "Requirement Updated Successfully!" : "Requirement Created Successfully!"
      );
      if (params.id == 0) {
        setTimeout(() => navigate("/talent-acquisition/requirements"), 1000);
      } else {
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  useEffect(() => {
    fetchDropdownOptions();
    if (params.id == 0) fetchNextRequestedId();
  }, []);

  useEffect(() => {
    if (params.id != 0) fetchDetails();
  }, [params]);

  const inputFields = [
    {
      type: "TextField",
      label: "Requested ID *",
      stateKey: "requestedId",
      // inputType: "number",
      disabled: true,
    },
    {
      type: "DatePicker",
      label: "Date Raised *",
      stateKey: "DateRaised",
    },
    {
      type: "Autocomplete",
      label: "Client *",
      stateKey: "Client",
      options: "clients",
      valueKey: "companyName",
      getOptionLabelKey: "companyName",
    },
    {
      type: "Autocomplete",
      label: "Position Title *",
      stateKey: "positionTitle",
      options: positionOptions,
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Discipline *",
      stateKey: "Discipline",
      options: disciplines,
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Employment Type *",
      stateKey: "employementType",
      options: employmentTypes,
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Location *",
      stateKey: "location",
    },
    {
      type: "TextField",
      label: "No. of Openings *",
      stateKey: "openingNo",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Budget Min *",
      stateKey: "budetMin",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Budget Max *",
      stateKey: "budgetMax",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "JD Reference *",
      stateKey: "jdRef",
    },
    {
      type: "Autocomplete",
      label: "Priority *",
      stateKey: "priority",
      options: priorities,
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "DatePicker",
      label: "Target Date *",
      stateKey: "targetDate",
    },
    {
      type: "Autocomplete",
      label: "Request Status *",
      stateKey: "RequestStatus",
      options: reqStatuses,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Recruiter Assigned *",
      stateKey: "recruiterAssigned",
      options: recruiters,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Days Open",
      stateKey: "dayopen",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Positions Filled",
      stateKey: "positionsField",
      inputType: "number",
    },
    {
      type: "Textarea",
      label: "Notes",
      stateKey: "notes",
    },
  ];

  return (
    <div>
     <Text variant="h3" bold className="text-blue-600">
        {params.id == 0 ? "Create Requirement" : "Update Requirement"}
      </Text> 
      <br />
      <CommonForm
        inputStates={inputStates}
        updateInputObject={updateInputObject}
        inputFields={inputFields}
      />
      <br />
       <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          id="btn-cancel"
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.allAccess) && (
          <SaveAndUpdate
            id="btn-save"
            label={params.id == 0 ? "Save" : "Update"}
            onClick={onSubmit}
          />
        )}
      </div> 
    </div>
  );
};

export default EditRequirements;
